import { api } from './api'

// ─── Costanti semantiche legate ai parametri worker (Zanshin step 4 spec) ────
// Worker: TICK_SECONDS=3.0, PENDING_CONFIRM_CYCLES=4 → max attesa 12s
export const PENDING_AGE_AMBER_S = 6   // PENDING_CONFIRM_CYCLES * TICK_SECONDS / 2
export const PENDING_AGE_RED_S   = 12  // PENDING_CONFIRM_CYCLES * TICK_SECONDS

// ─── Types ───────────────────────────────────────────────────────────────────
export type ZanshinStateValue =
  | 'HEDGE'
  | 'BROKER_SHIELD'
  | 'RUNNER_BROKER'
  | 'RUNNER_PROP'
  | 'RUNNER_ATTIVO'

export interface ZanshinOperation {
  operation_id: number
  zanshin_state: ZanshinStateValue
  shield_phase: 0 | 1 | 2
  prop_direction: 'BUY' | 'SELL' | null
  prop_entry_price: number | null
  prop_lots: number | null
  prop_sl_current: number | null
  expected_prop_sl: number | null
  prop_tp_current: number | null
  broker_entry_price: number | null
  broker_lots: number | null
  broker_sl_current: number | null
  expected_broker_sl: number | null
  broker_tp_current: number | null
  is_pending: boolean
  pending_age_s: number | null
  stale_cycles: number
  last_decision_ts: string | null
  last_decision_action: string | null
  last_decision_event: string | null
  last_decision_reason: string | null
  status_updated_at: string | null
}

export interface ZanshinPair {
  pair_id: number
  display_name: string
  prop_provider_label: string
  prop_phase_label: string
  prop_ea_connected: boolean
  broker_ea_connected: boolean
  operation: ZanshinOperation | null
  user_id?: number  // solo vista admin
}

export interface PairsStatusResponse {
  pairs: ZanshinPair[]
  totals: { total: number; with_op: number; pending: number }
  server_ts: string
}

// ─── Tipi vista admin technical (status legacy) ──────────────────────────────
export interface ZanshinWorker {
  enabled: boolean
  task_alive: boolean
  tick_seconds: number
  pending_count: number
  pair_locks_count: number
  consecutive_errors: number
  circuit_breakers_open: string[]
}
export interface ZanshinStatusResponse {
  worker: ZanshinWorker
  active_operations: any[]
  totals: { active: number; pending: number; force_close_24h: number }
  server_ts: string
}
export interface ZanshinDecision {
  id: number
  operation_id: number
  ts: string
  event_type: 'DISPATCHED' | 'CONFIRMED' | 'FORCE_CLOSE'
  from_state: string | null
  to_state: string | null
  action: string | null
  shield_phase: number | null
  new_prop_sl: number | null
  new_broker_sl: number | null
  reason: string | null
}

// ─── Tipi Approvals (modalita' MANUAL per pair) ──────────────────────────────

export type ZanshinMode = 'AUTO' | 'MANUAL'

export interface ZanshinApprovalItem {
  id: number
  operation_id: number
  pair_id: number
  summary: string | null
  action: string
  next_state: string | null
  new_broker_sl: number | null
  new_broker_tp: number | null
  new_prop_sl: number | null
  new_prop_tp: number | null
  close_broker: boolean
  close_prop: boolean
  reason: string
  created_at: string  // ISO UTC
  expires_at: string  // ISO UTC
  seconds_left: number
}

export interface ZanshinApprovalsListResponse {
  items: ZanshinApprovalItem[]
  server_ts: string
}

export interface ZanshinApprovalActionResponse {
  ok: boolean
  error?: string | null
  sent_any?: boolean | null
  op_id?: number | null
}

export const zanshinService = {
  // ── Vista card (UI principale) ─────────────────────────────────────────────
  getAdminPairsStatus: () => api.silent.get<PairsStatusResponse>('/admin/zanshin/pairs-status'),
  getMyPairsStatus:    () => api.silent.get<PairsStatusResponse>('/ixen/zanshin/my-pairs-status'),

  // ── Vista tecnica admin (worker health + decisioni) ────────────────────────
  getStatus: () => api.silent.get<ZanshinStatusResponse>('/admin/zanshin/status'),
  getDecisions: (params?: { op_id?: number; event_type?: string; limit?: number }) => {
    const q = new URLSearchParams()
    if (params?.op_id) q.set('op_id', String(params.op_id))
    if (params?.event_type) q.set('event_type', params.event_type)
    if (params?.limit) q.set('limit', String(params.limit))
    const qs = q.toString()
    return api.silent.get<ZanshinDecision[]>(`/admin/zanshin/decisions${qs ? '?' + qs : ''}`)
  },

  // ── Modalita' MANUAL: approvals per pair ───────────────────────────────────
  // GET silent: polling 5s — non spammare toast di errore.
  listApprovals: () =>
    api.silent.get<ZanshinApprovalsListResponse>('/zanshin/approvals'),

  approveRequest: (requestId: number) =>
    api.post<ZanshinApprovalActionResponse>(`/zanshin/approvals/${requestId}/approve`),

  rejectRequest: (requestId: number) =>
    api.post<ZanshinApprovalActionResponse>(`/zanshin/approvals/${requestId}/reject`),

  setPairMode: (pairId: number, mode: ZanshinMode) =>
    api.patch<{ pair_id: number; zanshin_mode: ZanshinMode }>(
      `/zanshin/pairs/${pairId}/mode`,
      { mode },
    ),
}
